import { Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, AlertCircle, X, KeyRound } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import logoSecondLaneDark from '@/assets/logoSecondLaneDark.svg'
import type { LoginScreenBaseProps } from './LoginScreenBase'

export const LoginScreenMobile = ({
  formData,
  loginMode,
  otpSent,
  showPassword,
  rememberMe,
  loading,
  sendingOtp,
  error,
  onFormDataChange,
  onLoginModeChange,
  onSendOtp,
  onShowPasswordChange,
  onRememberMeChange,
  onClearField,
  onSubmit,
}: LoginScreenBaseProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="relative min-h-screen bg-white px-6 pb-8 pt-6 dark:bg-[#0d0d0d]">
      <div className="pointer-events-none absolute inset-0 hidden dark:block [background:radial-gradient(680px_420px_at_18%_24%,rgba(255,255,255,0.08),transparent_58%),radial-gradient(420px_280px_at_24%_70%,rgba(255,255,255,0.04),transparent_65%)]" />
      <div className="mx-auto max-w-[402px]">
        <div className="mb-12 flex justify-center">
          <img src={logoSecondLane} alt="SecondLane" className="h-6 w-24 dark:hidden" />
          <img src={logoSecondLaneDark} alt="SecondLane" className="hidden h-6 w-24 dark:block" />
        </div>

        <div className="mb-12 text-center">
          <h1 className="font-plus-jakarta text-[30px] font-medium leading-[40px] tracking-[-0.28px] text-[#2d2923] dark:text-white">
            Welcome Back
          </h1>
          <p className="font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
            Sign in to your SecondLane account
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-4 py-3 text-red-500">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-3">
            <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
              Email Address
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              leftElement={<Mail size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
              rightElement={formData.email ? (
                <button
                  type="button"
                  onClick={() => onClearField('email')}
                  className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                >
                  <X size={18} />
                </button>
              ) : undefined}
              className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
              required
              disabled={otpSent && loginMode === 'otp'}
            />
          </div>

          {loginMode === 'otp' ? (
            <>
              {otpSent && (
                <div className="space-y-3">
                  <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                    OTP Code
                  </label>
                  <Input
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="Enter OTP code"
                    leftElement={<KeyRound size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                    rightElement={formData.otp ? (
                      <button
                        type="button"
                        onClick={() => onClearField('otp')}
                        className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                      >
                        <X size={18} />
                      </button>
                    ) : undefined}
                    className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
                    required
                    maxLength={6}
                  />
                </div>
              )}

              <Button
                type={otpSent ? 'submit' : 'button'}
                variant="primary"
                onClick={otpSent ? undefined : onSendOtp}
                className="h-12 w-full rounded-none bg-[rgba(45,41,35,0.72)] font-plus-jakarta text-[14px] font-medium leading-[20px] text-white hover:bg-[rgba(45,41,35,0.85)] hover:text-white dark:bg-[rgba(255,255,255,0.72)] dark:text-[#2d2923] dark:hover:bg-[rgba(255,255,255,0.85)] dark:hover:text-[#2d2923]"
                isLoading={otpSent ? loading : sendingOtp}
                disabled={!formData.email || sendingOtp}
              >
                {otpSent ? 'SIGN IN' : 'SEND OTP CODE'}
              </Button>

              <div className="space-y-3 text-center">
                <button
                  type="button"
                  onClick={() => onLoginModeChange('password')}
                  className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] underline decoration-solid dark:text-white"
                >
                  Sign in with password
                </button>
                {otpSent && (
                  <button
                    type="button"
                    onClick={onSendOtp}
                    className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] underline decoration-solid dark:text-[rgba(255,255,255,0.64)]"
                    disabled={sendingOtp}
                  >
                    {sendingOtp ? 'Sending...' : 'Resend OTP code'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Password
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => onShowPasswordChange(!showPassword)}
                      className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    id="remember-me-mobile"
                    checked={rememberMe}
                    onCheckedChange={(checked) => onRememberMeChange(checked as boolean)}
                    className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:bg-[#2d2923] data-[state=checked]:border-[rgba(255,255,255,0.82)] dark:bg-[rgba(28,28,28,0.56)] dark:border-[rgba(253,253,253,0.32)] dark:data-[state=checked]:bg-[#ecebeb] dark:data-[state=checked]:border-[rgba(45,41,35,0.82)] dark:data-[state=checked]:[&>span>svg]:text-[#2d2923]"
                  />
                  <span className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="block text-center font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] underline decoration-solid dark:text-[rgba(253,253,253,0.72)]"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="h-12 w-full rounded-none bg-[rgba(45,41,35,0.72)] font-plus-jakarta text-[14px] font-medium leading-[20px] text-white hover:bg-[rgba(45,41,35,0.85)] hover:text-white dark:bg-[rgba(255,255,255,0.72)] dark:text-[#2d2923] dark:hover:bg-[rgba(255,255,255,0.85)] dark:hover:text-[#2d2923]"
                isLoading={loading}
              >
                SIGN IN
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => onLoginModeChange('otp')}
                  className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] underline decoration-solid dark:text-white"
                >
                  Sign in with OTP
                </button>
              </div>
            </>
          )}

          <p className="flex items-center justify-center gap-1 font-plus-jakarta text-[14px] font-normal leading-[20px]">
            <span className="text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">Don&apos;t have an account?</span>
            <Link to="/signup" className="text-[#2d2923] underline decoration-solid dark:text-white">
              Sign up
            </Link>
          </p>
        </form>

        <p className="mt-8 text-center font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="underline decoration-solid text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline decoration-solid text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  )
}
